// src/i18n/i18n.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { languages, dateLocales } from './config';
import { format, formatDistance, formatRelative } from 'date-fns';

interface I18nContextType {
  language: string;
  setLanguage: (lang: string) => void;
  direction: 'ltr' | 'rtl';
  t: (key: string, options?: any) => string;
  formatDate: (date: Date | number, formatStr?: string) => string;
  formatDistance: (date: Date | number, baseDate?: Date | number) => string;
  formatRelative: (date: Date | number, baseDate?: Date | number) => string;
  formatNumber: (num: number, options?: Intl.NumberFormatOptions) => string;
  formatCurrency: (amount: number, currency?: string) => string;
  languages: typeof languages;
  isRTL: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const [language, setLanguageState] = useState(i18n.language || 'en');
  const [direction, setDirection] = useState<'ltr' | 'rtl'>('ltr');

  useEffect(() => {
    const lang = languages.find(l => l.code === language) || languages[0];
    setDirection(lang.dir as 'ltr' | 'rtl');
    document.documentElement.dir = lang.dir;
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setLanguageState(lang);
    localStorage.setItem('i18nextLng', lang);
  };

  const formatDate = (date: Date | number, formatStr = 'PPP') => {
    const locale = dateLocales[language] || dateLocales.en;
    return format(date, formatStr, { locale });
  };

  const formatDistanceToNow = (date: Date | number, baseDate?: Date | number) => {
    const locale = dateLocales[language] || dateLocales.en;
    return formatDistance(date, baseDate || new Date(), { locale, addSuffix: true });
  };

  const formatRelativeTime = (date: Date | number, baseDate?: Date | number) => {
    const locale = dateLocales[language] || dateLocales.en;
    return formatRelative(date, baseDate || new Date(), { locale });
  };

  const formatNumber = (num: number, options?: Intl.NumberFormatOptions) => {
    return new Intl.NumberFormat(language, options).format(num);
  };

  const formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat(language, {
      style: 'currency',
      currency
    }).format(amount);
  };

  return (
    <I18nContext.Provider
      value={{
        language,
        setLanguage,
        direction,
        t,
        formatDate,
        formatDistance: formatDistanceToNow,
        formatRelative: formatRelativeTime,
        formatNumber,
        formatCurrency,
        languages,
        isRTL: direction === 'rtl'
      }}
    >
      <div dir={direction}>{children}</div>
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};